import { hasAppInteriorGrant, isPathwayOnlyAccessState } from '@/features/auth/accessPolicy'

import { buildToolCards } from '../config/toolMatrix'
import type {
  ActiveMissionSnapshot,
  ActiveSurfaceSnapshot,
  ActivityWitnessEntry,
  ActivityWitnessFeed,
  BestNextMoveBundle,
  BestNextMoveRecommendation,
  CommandDeckHoldState,
  CommandDeckIdentityContext,
  CommandDeckMissionContext,
  CommandDeckMissionResolverHint,
  CommandDeckPersonalizationState,
  CommandDeckResolverInput,
  CommandDeckSurfaceContext,
  MissionMembershipRole,
  PrimarySurface,
  ProjectProgressSnapshot,
  SurfaceReadinessMap,
  SurfaceResolutionSource,
  ToolCardModel,
  UserAccessSnapshot,
} from '../contracts/types'
import {
  BNM_VISITOR,
  EMPTY_MISSION_BNM,
  EMPTY_MISSION_ID,
  EMPTY_MISSION_PROGRESS,
  getMissionPreset,
  HELD_MISSION_PROGRESS,
} from '../fixtures/missionPresets'
import { NOTICES_CONTROLLED_ACCESS, NOTICES_VISITOR } from '../fixtures/activityNotices'
import type { ActivityNotice } from '../contracts/types'
import {
  deriveSurfaceConstellation,
  getAccessLabel,
} from '../missionControl/missionControlModel'

const ROUTE_TO_SURFACE: Record<string, PrimarySurface> = {
  '/guided-studio': 'guided_studio',
  '/omnistudio': 'omnistudio',
  '/omnicine': 'omnicine',
  '/omnicast': 'omnicast',
  '/guided/start-from-world': 'guided_studio',
  '/wearables/connect': 'none',
  '/command-deck': 'none',
  '/command-deck/preview': 'none',
}

const SURFACE_LABELS: Record<PrimarySurface, string> = {
  guided_studio: 'Guided Studio',
  omnistudio: 'OmniStudio',
  omnicine: 'OmniCine',
  omnicast: 'OmniCast',
  scapes_worlds: 'Scapes',
  xr_worlds: 'XR',
  rights_passport: 'Rights Passport',
  request_access: 'Request Access',
  book_demo: 'Book Demo',
  none: 'Unresolved',
}

const SURFACE_ROUTES: Partial<Record<PrimarySurface, string>> = {
  guided_studio: '/guided-studio',
  omnistudio: '/omnistudio',
  omnicine: '/omnicine',
  omnicast: '/omnicast',
}

const ROLE_LABELS: Record<MissionMembershipRole, string> = {
  founder: 'Founder',
  writer: 'Writer',
  worldbuilder: 'Worldbuilder',
  producer: 'Producer',
  cinematic_editor: 'Cinematic Editor',
  reviewer: 'Reviewer / Witness',
  witness: 'Witness',
  operator: 'Operator',
  creator: 'Creator',
  visitor: 'Visitor',
  unknown: 'Creator',
}

function resolveHoldState(access: UserAccessSnapshot, accessProfileId: string): CommandDeckHoldState {
  const unlocked = hasAppInteriorGrant(access)
  if (accessProfileId === 'revoked' || (access.accessState === 'visitor' && !unlocked)) {
    return {
      posture: accessProfileId === 'revoked' ? 'revoked' : 'visitor',
      unlocked: false,
      headline: 'Access held — mission interiors are not available',
    }
  }
  if (!unlocked && isPathwayOnlyAccessState(access.accessState)) {
    return {
      posture: 'pathway',
      unlocked: false,
      headline: 'Pathway active — grant required to unlock Command Deck interiors',
    }
  }
  if (!unlocked) {
    return {
      posture: 'visitor',
      unlocked: false,
      headline: 'Mission control interiors require an active grant',
    }
  }
  return { posture: 'unlocked', unlocked: true }
}

function inferMembershipRole(
  access: UserAccessSnapshot,
  hint?: CommandDeckMissionResolverHint
): MissionMembershipRole {
  if (hint?.membershipRole) return hint.membershipRole
  if (access.accessState === 'founder_mode') return 'founder'
  if (access.accessState === 'visitor') return 'visitor'
  if (hasAppInteriorGrant(access)) return 'creator'
  return 'unknown'
}

function resolveActiveMissionId(
  access: UserAccessSnapshot,
  hold: CommandDeckHoldState,
  hint?: CommandDeckMissionResolverHint
): string | null {
  if (!hold.unlocked) return null
  if (hint?.missionId !== undefined) {
    const id = hint.missionId
    return id === null || id === EMPTY_MISSION_ID ? null : id
  }
  const fromAccess = access.lastActiveProjectId?.trim()
  if (!fromAccess || fromAccess === EMPTY_MISSION_ID) return null
  return fromAccess
}

function adjustBestNextMoveForRole(
  bundle: BestNextMoveBundle,
  role: MissionMembershipRole,
  activeSurface: PrimarySurface
): BestNextMoveBundle {
  const primary = { ...bundle.primary }
  if (role === 'writer') {
    primary.title = 'Ground scene flow and narrative packets'
    primary.summary = 'Continue shaping scenes and character continuity before timeline or presentation paths.'
    primary.suggestedSurface = activeSurface === 'none' ? 'guided_studio' : activeSurface
    primary.category = 'continue_building'
  } else if (role === 'worldbuilder') {
    primary.title = 'Define world anchors and spatial rules'
    primary.summary = 'Strengthen Scape locations, environment anchors, and XR readiness gates.'
    primary.suggestedSurface = 'scapes_worlds'
    primary.category = 'prepare_scape_world'
  } else if (role === 'producer') {
    primary.title = 'Prepare structure package for OmniStudio handoff'
    primary.summary = 'Review compile readiness, missing dependencies, and handoff blockers.'
    primary.suggestedSurface = 'omnistudio'
    primary.category = 'prepare_handoff'
  } else if (role === 'cinematic_editor') {
    primary.title = 'Close timeline gaps before presentation'
    primary.summary = 'Sequence order and cinematic presentation risk need review in OmniCine.'
    primary.suggestedSurface = 'omnicine'
    primary.category = 'prepare_timeline'
  } else if (role === 'reviewer') {
    primary.title = 'Review witness presentation readiness'
    primary.summary = 'Read-only witness posture — confirm presentation visibility and feedback boundaries.'
    primary.suggestedSurface = 'omnicast'
    primary.category = 'review_stage'
    primary.humanConfirmationRequired = true
  }
  return { ...bundle, primary }
}

function mapRouteToSurface(route: string | null | undefined): PrimarySurface | null {
  if (!route) return null
  const path = route.split('?')[0] ?? route
  if (ROUTE_TO_SURFACE[path]) return ROUTE_TO_SURFACE[path]
  for (const [prefix, surface] of Object.entries(ROUTE_TO_SURFACE)) {
    if (path.startsWith(`${prefix}/`)) return surface
  }
  return null
}

function resolveActiveSurface(
  progress: ProjectProgressSnapshot,
  bnm: BestNextMoveRecommendation,
  hold: CommandDeckHoldState,
  hint?: CommandDeckMissionResolverHint
): ActiveSurfaceSnapshot {
  if (!hold.unlocked || progress.projectId === EMPTY_MISSION_ID) {
    return {
      surfaceId: 'none',
      label: SURFACE_LABELS.none,
      routePath: null,
      resolutionSource: 'unresolved',
    }
  }

  let surfaceId: PrimarySurface = 'none'
  let resolutionSource: SurfaceResolutionSource = 'unresolved'

  if (hint?.forcedSurface) {
    surfaceId = hint.forcedSurface
    resolutionSource = 'bnm_recommendation'
  } else if (hint?.sessionSurfaceRoute) {
    const fromRoute = mapRouteToSurface(hint.sessionSurfaceRoute)
    if (fromRoute && fromRoute !== 'none') {
      surfaceId = fromRoute
      resolutionSource = 'session_route'
    }
  }

  if (surfaceId === 'none') {
    const focusStage = progress.spine.find((s) => s.stageId === progress.currentFocusStageId)
    if (focusStage?.primarySurface && focusStage.primarySurface !== 'none') {
      surfaceId = focusStage.primarySurface
      resolutionSource = 'focus_stage'
    }
  }

  if (surfaceId === 'none' && bnm.suggestedSurface && bnm.suggestedSurface !== 'none') {
    surfaceId = bnm.suggestedSurface
    resolutionSource = 'bnm_recommendation'
  }

  return {
    surfaceId,
    label: SURFACE_LABELS[surfaceId] ?? surfaceId,
    routePath: SURFACE_ROUTES[surfaceId] ?? null,
    resolutionSource,
  }
}

function buildNeutralWitnessFeed(mode: CommandDeckResolverInput['mode']): ActivityWitnessFeed {
  const prefix = mode === 'preview' ? 'Preview session' : 'Session'
  return {
    source: 'neutral',
    entries: [
      { time: '—', text: `${prefix} active — no mission events yet` },
      { time: '—', text: 'Select or start a mission to populate activity' },
    ],
  }
}

function buildWitnessFeed(
  notices: ActivityNotice[],
  missionId: string | null,
  hold: CommandDeckHoldState,
  mode: CommandDeckResolverInput['mode'],
  snapshotEntries?: ActivityWitnessEntry[]
): ActivityWitnessFeed {
  if (!hold.unlocked) {
    return {
      source: 'notices',
      entries: notices.slice(0, 4).map((n) => ({
        time: '—',
        text: n.title,
      })),
    }
  }
  if (!missionId || missionId === EMPTY_MISSION_ID) {
    return buildNeutralWitnessFeed(mode)
  }
  if (snapshotEntries?.length) {
    return { source: 'project_snapshot', entries: snapshotEntries }
  }
  return {
    source: 'neutral',
    entries: [],
  }
}

function buildReadinessMap(tools: ToolCardModel[]): SurfaceReadinessMap {
  const bySurfaceId: SurfaceReadinessMap['bySurfaceId'] = {}
  for (const tool of tools) {
    const surface =
      tool.toolId === 'scapes_worlds'
        ? 'scapes_worlds'
        : tool.toolId === 'xr_worlds'
          ? 'xr_worlds'
          : (tool.toolId as PrimarySurface)
    if (surface in SURFACE_LABELS) {
      bySurfaceId[surface] = { state: tool.availability, label: tool.label }
    }
  }
  return { bySurfaceId }
}

function buildSurfaceContexts(
  tools: ToolCardModel[],
  activeSurfaceId: PrimarySurface
): CommandDeckSurfaceContext[] {
  const nodes = deriveSurfaceConstellation(tools, activeSurfaceId)
  return nodes.map((node) => ({
    surfaceId: node.id,
    label: node.label,
    state: node.state,
    primarySurface:
      node.id === 'scapes'
        ? 'scapes_worlds'
        : node.id === 'xr'
          ? 'xr_worlds'
          : (node.id as PrimarySurface),
    routePath: SURFACE_ROUTES[node.id as PrimarySurface] ?? null,
  }))
}

function buildIdentity(
  access: UserAccessSnapshot,
  role: MissionMembershipRole,
  mode: CommandDeckResolverInput['mode'],
  hold: CommandDeckHoldState
): CommandDeckIdentityContext {
  const displayName = access.displayName?.trim() || 'Creator'
  return {
    displayName,
    roleDisplayLabel: ROLE_LABELS[role],
    accessLabel: getAccessLabel(access.accessState),
    operationalStatus:
      hold.posture === 'revoked'
        ? 'Revoked'
        : !hold.unlocked
          ? 'Held'
          : mode === 'preview'
            ? 'Preview'
            : 'Active',
  }
}

/**
 * Pure read-only resolver — no writes, grants, routing, or persistence.
 */
function applyLiveSnapshotOrEmpty(
  input: CommandDeckResolverInput,
  missionId: string | null
): { progress: ProjectProgressSnapshot; bestNextMove: BestNextMoveBundle } {
  const liveCtx = input.liveMissionContext
  if (input.mode === 'live' && liveCtx?.attempted) {
    const snapshot = liveCtx.snapshot
    if (!snapshot || !missionId || snapshot.projectId !== missionId) {
      return { progress: EMPTY_MISSION_PROGRESS, bestNextMove: EMPTY_MISSION_BNM }
    }
    return {
      progress: {
        ...snapshot.progress,
        projectId: snapshot.projectId,
        projectName: snapshot.title,
      },
      bestNextMove: snapshot.bestNextMove,
    }
  }
  const preset = getMissionPreset(missionId)
  return { progress: preset.progress, bestNextMove: preset.bestNextMove }
}

export function resolveCommandDeckPersonalizationState(
  input: CommandDeckResolverInput
): CommandDeckPersonalizationState {
  const { access, mode, accessProfileId, missionHint, liveMissionContext } = input
  const hold = resolveHoldState(access, accessProfileId)
  const tools = buildToolCards(access.accessState)
  const membershipRole = inferMembershipRole(access, missionHint)

  let progress: ProjectProgressSnapshot
  let bestNextMoveBundle: BestNextMoveBundle
  let notices: ActivityNotice[]

  if (!hold.unlocked) {
    progress =
      accessProfileId === 'visitor' || accessProfileId === 'revoked'
        ? { ...HELD_MISSION_PROGRESS, summary: { ...HELD_MISSION_PROGRESS.summary!, headline: hold.headline ?? HELD_MISSION_PROGRESS.summary!.headline } }
        : HELD_MISSION_PROGRESS
    bestNextMoveBundle = BNM_VISITOR
    notices =
      hold.posture === 'pathway' ? NOTICES_CONTROLLED_ACCESS : NOTICES_VISITOR
  } else {
    const missionId = resolveActiveMissionId(access, hold, missionHint)
    const applied = applyLiveSnapshotOrEmpty(input, missionId)
    progress = applied.progress
    bestNextMoveBundle = applied.bestNextMove
    notices = NOTICES_CONTROLLED_ACCESS
  }

  const missionId = hold.unlocked
    ? resolveActiveMissionId(access, hold, missionHint)
    : null

  const mission: ActiveMissionSnapshot = {
    missionId,
    missionName: progress.projectName,
    missionType: progress.projectType,
    membershipRole,
    isActive: Boolean(missionId && missionId !== EMPTY_MISSION_ID),
  }

  const surface = resolveActiveSurface(
    progress,
    bestNextMoveBundle.primary,
    hold,
    missionHint
  )

  if (hold.unlocked && mission.isActive) {
    bestNextMoveBundle = adjustBestNextMoveForRole(
      bestNextMoveBundle,
      membershipRole,
      surface.surfaceId
    )
  }

  const activity = buildWitnessFeed(
    notices,
    missionId,
    hold,
    mode,
    liveMissionContext?.snapshot?.witnessEntries
  )
  const readiness = buildReadinessMap(tools)
  const surfaces = buildSurfaceContexts(tools, surface.surfaceId)

  const missionContext: CommandDeckMissionContext = {
    progress,
    bestNextMove: bestNextMoveBundle,
  }

  const identity = buildIdentity(access, membershipRole, mode, hold)

  return {
    mode,
    userAccess: access,
    accessProfileId,
    hold,
    mission,
    surface,
    readiness,
    nextMove: hold.unlocked ? bestNextMoveBundle.primary : null,
    activity,
    surfaces,
    missionContext,
    identity,
  }
}
